import { Document, Packer, Paragraph, TextRun, HeadingLevel, PageBreak, Footer, PageNumber, AlignmentType, LevelFormat } from 'docx';
import { Book } from '../database.types';
import { ExportResult } from './types';
import { parseMarkdownBlocks, parseInlineFormatting, prepareChapters } from './markdown-parser';

function parseInlineToTextRuns(text: string): TextRun[] {
  const segments = parseInlineFormatting(text);
  return segments.map(seg => new TextRun({
    text: seg.text,
    bold: seg.bold || undefined,
    italics: seg.italic || undefined,
  }));
}

export async function generateDocx(book: Book): Promise<ExportResult> {
  const chapters = prepareChapters(book.chapters || []);

  const chapterParagraphs: Paragraph[] = [];

  chapters.forEach((chapter, chIdx) => {
    chapterParagraphs.push(
      new Paragraph({
        children: parseInlineToTextRuns(chapter.chapter_title),
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 400 },
      })
    );

    const content = (chapter.generated_text || '').trim();
    const blocks = parseMarkdownBlocks(content);

    for (const block of blocks) {
      if (block.type === 'heading') {
        const level = (block.level === 3) ? HeadingLevel.HEADING_3 : HeadingLevel.HEADING_2;
        chapterParagraphs.push(
          new Paragraph({
            children: parseInlineToTextRuns(block.text || ''),
            heading: level,
            spacing: { before: 300, after: 200 },
          })
        );
      } else if (block.type === 'paragraph') {
        chapterParagraphs.push(
          new Paragraph({
            children: parseInlineToTextRuns(block.text || ''),
            spacing: { after: 200 },
          })
        );
      } else if (block.type === 'bullet') {
        for (const item of block.items || []) {
          chapterParagraphs.push(
            new Paragraph({
              children: parseInlineToTextRuns(item),
              bullet: { level: 0 },
              spacing: { after: 80 },
            })
          );
        }
      } else if (block.type === 'numbered-list') {
        for (const item of block.items || []) {
          chapterParagraphs.push(
            new Paragraph({
              children: parseInlineToTextRuns(item),
              numbering: { reference: 'default-numbering', level: 0 },
              spacing: { after: 80 },
            })
          );
        }
      }
    }

    if (chIdx < chapters.length - 1) {
      chapterParagraphs.push(new Paragraph({ children: [new PageBreak()] }));
    }
  });

  // Notes/References section
  const referencesParagraphs: Paragraph[] = [];
  const chaptersWithRefs = chapters.filter(ch => ch.chapter_references && ch.chapter_references.trim());
  if (chaptersWithRefs.length > 0) {
    referencesParagraphs.push(new Paragraph({ children: [new PageBreak()] }));
    referencesParagraphs.push(
      new Paragraph({
        text: 'Notes',
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 400 },
      })
    );

    for (const chapter of chaptersWithRefs) {
      referencesParagraphs.push(
        new Paragraph({
          children: parseInlineToTextRuns(chapter.chapter_title),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 },
        })
      );

      const refs = chapter.chapter_references!.split('\n').filter((r: string) => r.trim());
      refs.forEach((ref: string, idx: number) => {
        referencesParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: `${idx + 1}. ${ref.trim()}` })],
            spacing: { after: 80 },
          })
        );
      });
    }
  }

  // Author bio
  const authorBioParagraphs: Paragraph[] = [];
  if (book.author_bio) {
    authorBioParagraphs.push(new Paragraph({ children: [new PageBreak()] }));
    authorBioParagraphs.push(
      new Paragraph({
        text: 'About the Author',
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 400 },
      })
    );
    const bioBlocks = parseMarkdownBlocks(book.author_bio);
    for (const block of bioBlocks) {
      if (block.type === 'paragraph') {
        authorBioParagraphs.push(
          new Paragraph({ children: parseInlineToTextRuns(block.text || ''), spacing: { after: 200 } })
        );
      } else if (block.type === 'heading') {
        authorBioParagraphs.push(
          new Paragraph({ children: parseInlineToTextRuns(block.text || ''), heading: HeadingLevel.HEADING_2, spacing: { before: 200, after: 200 } })
        );
      }
    }
  }

  // TOC
  const tocParagraphs: Paragraph[] = [
    new Paragraph({
      text: 'Table of Contents',
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 400 },
    }),
    ...chapters.map(chapter =>
      new Paragraph({
        children: [new TextRun({ text: chapter.chapter_title, font: 'Calibri' })],
        spacing: { after: 120 },
      })
    ),
    new Paragraph({ children: [new PageBreak()] }),
  ];

  const doc = new Document({
    numbering: {
      config: [{
        reference: 'default-numbering',
        levels: [{
          level: 0,
          format: LevelFormat.DECIMAL,
          text: '%1.',
          alignment: AlignmentType.START,
        }],
      }],
    },
    styles: {
      default: {
        heading1: {
          run: { size: 32, bold: true, font: 'Calibri' },
          paragraph: { spacing: { after: 400 }, alignment: AlignmentType.CENTER },
        },
        heading2: {
          run: { size: 28, bold: true, font: 'Calibri' },
          paragraph: { spacing: { before: 300, after: 200 } },
        },
        heading3: {
          run: { size: 24, bold: true, font: 'Calibri' },
          paragraph: { spacing: { after: 200 } },
        },
      },
    },
    sections: [
      // Title + Copyright (no page numbers)
      {
        properties: {
          page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } },
        },
        children: [
          new Paragraph({ text: book.title, heading: HeadingLevel.HEADING_1, spacing: { before: 3000, after: 400 } }),
          ...(book.subtitle ? [new Paragraph({ text: book.subtitle, heading: HeadingLevel.HEADING_2, spacing: { after: 400 } })] : []),
          ...(book.author ? [new Paragraph({ text: `by ${book.author}`, spacing: { after: 800 }, alignment: AlignmentType.CENTER })] : []),
          new Paragraph({ children: [new PageBreak()] }),
          new Paragraph({
            children: [new TextRun('Copyright (c) ' + new Date().getFullYear() + ' '), new TextRun(book.author || 'All rights reserved')],
            spacing: { after: 200 },
          }),
          new Paragraph({ text: 'All rights reserved.', spacing: { after: 200 } }),
          new Paragraph({
            text: 'This book or any portion thereof may not be reproduced or used in any manner whatsoever without the express written permission of the publisher except for the use of brief quotations in a book review.',
            spacing: { after: 400 },
          }),
        ],
      },
      // TOC + Chapters + Author Bio (with page numbers)
      {
        properties: {
          page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({ children: [PageNumber.CURRENT] })],
              }),
            ],
          }),
        },
        children: [
          ...tocParagraphs,
          ...chapterParagraphs,
          ...referencesParagraphs,
          ...authorBioParagraphs,
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  return {
    filename: `${book.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`,
    blob,
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  };
}
